// HST — Adaptateur entre le store du jeu (Zustand, gameStore) et les composants
// DepotVue3D / FriseChronologique / ArbreDeveloppement.
//
// Écrit d'après le code HST (/app/frontend/src) : investissements `inv-depot-*` /
// `inv-energy-*`, ownedDepots (QG + créés + rachetés), getDepotEffectiveInvestments,
// conditionCheck des investissements, flotte par depotId, contrats servingDepotId.
// À placer dans src/components/hst-depot3d/ (chemins d'import relatifs ci-dessous).

import { INVESTMENTS } from '../../data/investments';
import { UPGRADE_TREE_NODES } from '../../data/depotUpgradeTree';
import { DEPOT_PHASE_INVESTMENTS, getDepotEffectiveInvestments } from '../../data/depotPhases';
import { getDepotCityId } from '../../data/depotGeo';
import { findCityById } from '../../data/mapCities';
import { COMPETITORS } from '../../data/competitors';
import { VEHICLES } from '../../data/vehicles';
import { localize } from '../../data/i18nData';
import { tStandalone } from '../../i18n';

// ── Correspondance investissement du jeu ↔ zone 3D ─────────────────────────────
export const INV_TO_ZONE = {
  'inv-depot-01': 'P2', 'inv-depot-02': 'P3', 'inv-depot-04': 'P4', 'inv-depot-03': 'P5',
  'inv-depot-06': 'EXT', 'inv-depot-12': 'P11', 'inv-energy-01': 'P14', 'inv-depot-05': 'P6',
  'inv-depot-07': 'P7', 'inv-depot-08': 'P8', 'inv-depot-11': 'P9', 'inv-energy-02': 'P10',
  'inv-depot-10': 'P12', 'inv-depot-solar': 'P13',
};
export const ZONE_TO_INV = Object.fromEntries(Object.entries(INV_TO_ZONE).map(([a, b]) => [b, a]));
const ICONS = { P2: 'wrench', P3: 'warehouse', P4: 'dock', P5: 'building', EXT: 'globe', P11: 'wrench', P14: 'fuel', P6: 'thermo', P7: 'helmet', P8: 'box', P9: 'plug', P10: 'drop', P12: 'warning', P13: 'sun' };
const TIERS = ['Fondation', 'Structure', 'Développement', 'Spécialisation', 'Excellence'];

// Arbre au format des composants, construit à partir des données du jeu
// (noms, coûts, charges, conditions, liens affichés de depotUpgradeTree).
// 21/10/2026 — noms / effets / conditions localisés (overlay EN
// data/translations/investments_en.json via localize()). Le paramètre
// `language` ne sert qu'à invalider les memos React : localize() lit la
// langue courante dans le store i18n.
export function buildDepotTree(language = null) { // eslint-disable-line no-unused-vars
  return UPGRADE_TREE_NODES.filter((n) => INV_TO_ZONE[n.id]).map((n) => {
    const inv = localize('investment', INVESTMENTS.find((i) => i.id === n.id) || {});
    return {
      id: INV_TO_ZONE[n.id],
      invId: n.id,
      name: inv.name,
      tier: TIERS[n.layer] || 'Excellence',
      icon: ICONS[INV_TO_ZONE[n.id]],
      cost: inv.costBuy || 0,
      monthly: inv.chargeMonthly || 0,
      requires: (n.parents || []).map((p) => INV_TO_ZONE[p]).filter(Boolean),
      effect: inv.impactRevenue || '',
      condition: inv.condition || '',
    };
  });
}

// ── Profil du site d'après sa ville ────────────────────────────────────────────
const CITY_ENV = {
  paris: 'urbain', lyon: 'urbain', marseille: 'portuaire', bordeaux: 'tempere', lille: 'tempere',
  nantes: 'tempere', strasbourg: 'tempere', toulouse: 'mediterraneen', rennes: 'tempere',
  nice: 'mediterraneen', montpellier: 'mediterraneen', clermont: 'montagne', dijon: 'tempere',
  barcelona: 'mediterraneen', madrid: 'mediterraneen', lisbon: 'portuaire', porto: 'portuaire',
  frankfurt: 'urbain', munich: 'montagne', milan: 'urbain', rome: 'mediterraneen', brussels: 'urbain',
  amsterdam: 'portuaire', london: 'urbain', casablanca: 'portuaire', zurich: 'montagne', istanbul: 'portuaire',
};
const lighten = (hex, k = 0.55) => {
  const n = parseInt(hex.slice(1), 16);
  const c = [(n >> 16) & 255, (n >> 8) & 255, n & 255].map((v) => Math.round(v + (255 - v) * k));
  return '#' + c.map((v) => v.toString(16).padStart(2, '0')).join('');
};
const hash = (s) => [...String(s)].reduce((a, ch) => (a * 31 + ch.charCodeAt(0)) >>> 0, 7);

// ── Véhicules : modèle du catalogue → silhouette 3D ───────────────────────────
const VEHICLE_SHAPE = {
  'valrek-maxor-l2h2': ['vul', 'fourgon'], 'mantor-springo-xl': ['vul', 'fourgon'], 'valrek-maxor-frigo': ['vul', 'frigo'],
  'ivectra-daily-70c': ['porteur', 'tautliner'], 'mantor-tga-frigo-7t': ['porteur', 'frigo'], 'ivectra-eurocargo-120e': ['porteur', 'benne'],
  'scanex-r500-highline': ['semi', 'tautliner'], 'volvax-fh-frigo-lt': ['semi', 'frigo'], 'mantor-actrix-benne-44t': ['semi', 'benne'],
  'volvax-fx-frigo-arctic': ['semi', 'frigo'], 'mantor-actrix-adr': ['semi', 'citerne'], 'scanex-r650-v8-prestige': ['semi', 'tautliner'],
  'dafora-xf-porte-engins': ['semi', 'plateau'], 'volvax-fx-electric': ['semi', 'tautliner'], 'mantor-heavyhaul-xxl': ['semi', 'plateau'],
};
const shapeOf = (v) => {
  if (VEHICLE_SHAPE[v.dataId]) return VEHICLE_SHAPE[v.dataId];
  const sp = (v.specialties || []).join(' ');
  const type = (v.tier || 1) <= 1 ? 'vul' : v.tier === 2 ? 'porteur' : 'semi';
  const body = /ADR/.test(sp) ? 'citerne' : /Frigo/.test(sp) ? 'frigo' : /BTP/.test(sp) ? 'benne' : type === 'vul' ? 'fourgon' : 'tautliner';
  return [type, body];
};
// statut du jeu → présence sur le site
const STATUS = { idle: 'stationne', selling: 'stationne', active: 'mission', delivery: 'mission', transfer: 'mission', maintenance: 'atelier', broken: 'atelier' };

// ── Contrats : nature de la marchandise ───────────────────────────────────────
const goodsOf = (c) => {
  const veh = VEHICLES.find((v) => v.id === c.vehicleRequired);
  const sp = (veh?.specialties || []).join(' ') + ' ' + (c.requiresSpecialty || '') + ' ' + (c.category || '');
  if (/ADR/.test(sp) || /ADR|HazRoute/.test(c.condition || '')) return 'adr';
  if (/Frigo/.test(sp)) return 'frigo';
  if (/BTP/.test(sp)) return 'btp';
  return 'sec';
};

/**
 * Construit l'objet `depot` des composants pour un dépôt du jeu.
 * @param {object} s       état complet du store (useGameStore.getState() ou sélection)
 * @param {string} depotId id d'un élément de s.ownedDepots
 */
export function toDepot3D(s, depotId) {
  const d = (s.ownedDepots || []).find((x) => x.id === depotId);
  if (!d) return null;
  const isHQ = !!d.isHeadquarters;
  const tree = buildDepotTree();

  // investissements du site (même règle que pages/Depot.jsx)
  const invMap = isHQ ? (s.activeInvestments || {}) : getDepotEffectiveInvestments(d);
  const built = Object.keys(invMap).filter((k) => invMap[k] && INV_TO_ZONE[k]).map((k) => INV_TO_ZONE[k]);
  // Anciennes sauvegardes : les évolutions héritées (quai, frigo, atelier…) sont mappées
  // sans leurs étapes de base. Une zone construite implique ses prérequis affichés.
  for (let i = 0; i < built.length; i++) {
    for (const r of tree.find((n) => n.id === built[i])?.requires || []) if (!built.includes(r)) built.push(r);
  }

  // disponibilité réelle : conditionCheck du jeu, évaluée sur les investissements du site
  const checkState = isHQ ? s : { ...s, activeInvestments: invMap };
  const available = tree.filter((n) => !built.includes(n.id)).filter((n) => {
    const inv = INVESTMENTS.find((i) => i.id === n.invId);
    try { return typeof inv?.conditionCheck === 'function' ? !!inv.conditionCheck(checkState) : true; } catch { return false; }
  }).map((n) => n.id);

  // ville, pays, environnement
  const cityId = getDepotCityId(d, s.company);
  const city = findCityById(cityId);

  // rachat : zones présentes au closing = premiers jalons de la progression
  const comp = d.acquired ? COMPETITORS.find((c) => c.id === (d.acquiredFrom || d.brand)) : null;
  const acquired = d.acquired
    ? DEPOT_PHASE_INVESTMENTS.map(([inv]) => inv).filter((inv) => invMap[inv]).slice(0, d.seedInvestmentCount || 0).map((inv) => INV_TO_ZONE[inv]).filter(Boolean)
    : [];

  // frise : jour d'achat de chaque investissement
  const days = s.investmentPurchaseDays || {};
  const history = { P1: d.openedDay || 0 };
  // investmentPurchaseDays ne garde que le 1er achat (tous sites confondus) : pour un
  // dépôt secondaire, une date antérieure à son ouverture n'est pas la sienne → « — ».
  built.forEach((z) => {
    const day = days[ZONE_TO_INV[z]];
    if (d.acquired && acquired.includes(z)) history[z] = d.openedDay || 0;
    else if (day != null && (isHQ || day >= (d.openedDay || 0))) history[z] = day;
  });

  // flotte, chauffeurs, contrats rattachés au site
  const vehicles = (s.vehicles || []).filter((v) => (v.depotId || 'depot-hq') === d.id);
  const drivers = (s.drivers || []).filter((x) => (x.depotId || 'depot-hq') === d.id);
  const vehIds = new Set(vehicles.map((v) => v.id));
  const contracts = (s.activeContracts || []).filter((c) => (c.servingDepotId ? c.servingDepotId === d.id : vehIds.has(c.vehicleId)));

  return {
    id: d.id,
    name: (isHQ && (!d.name || d.name === 'QG')) ? `${tStandalone('d3_hq_prefix')} ${city?.name || ''}`.trim() : (d.name || `${tStandalone('d3_depot_prefix')} ${city?.name || ''}`.trim()),
    role: isHQ ? tStandalone('d3_role_hq') : '',
    cash: s.treasury,
    week: s.totalDays || 0,
    timeUnit: 'jour',
    country: city?.country || 'FR',
    environment: CITY_ENV[cityId] || 'tempere',
    mirror: !isHQ && hash(d.id) % 2 === 1,
    origin: d.acquired ? 'rachat' : 'creation',
    formerOwner: comp ? { name: comp.name, color: comp.color, accent: lighten(comp.color) } : null,
    acquired,
    built,
    available,
    history,
    slots: Math.min(7, Math.max(4, d.capacityVehicles || 4)),
    contracts: contracts.map((c) => ({
      id: c.instanceId || c.id,
      client: c.clientName || c.name,
      goods: goodsOf(c),
      volume: Math.max(2, Math.min(7, Math.round((c.dailyRevenue || c.revenue || 400) / 300))),
      route: c.name,
    })),
    vehicles: vehicles.filter((v) => v.status !== 'selling').map((v) => {
      const [type, body] = shapeOf(v);
      const drv = (s.drivers || []).find((x) => x.assignedVehicleId === v.id);
      const ctr = (s.activeContracts || []).find((c) => c.vehicleId === v.id);
      return { id: v.id, plate: v.plate || v.registration || v.id.slice(-6).toUpperCase(), brand: v.brand, model: `${v.brand} ${v.model}`, type, body, driver: drv?.name || null, status: STATUS[v.status] || 'stationne', contract: ctr ? (ctr.instanceId || ctr.id) : null };
    }),
    // HST n'a pas de rôles « mécanicien » ni « cariste » : ils sont figurés en 3D
    // quand l'atelier / l'entrepôt existent, sans alerte de cohérence.
    staff: {
      chauffeurs: drivers.length,
      exploitants: (d.managerId && s.hiredDepotManagers?.[d.managerId] ? 1 : 0) + (isHQ ? Object.keys(s.hiredSupport || {}).length : 0),
      mecaniciens: built.includes('P11') ? 2 : 0,
      caristes: built.includes('P3') ? 2 : 0,
      agents: 1,
    },
    staffChecks: ['chauffeurs'],
  };
}

/** Action « Construire » : même chemin que InvestmentPreviewModal. */
export function buildFor(s, depotId, zoneId) {
  const invId = ZONE_TO_INV[zoneId];
  const d = (s.ownedDepots || []).find((x) => x.id === depotId);
  if (!invId || !d) return;
  if (d.isHeadquarters) s.activateInvestment(invId);
  else s.activateDepotInvestment(invId, d.id);
}
